import { AddressType } from '@/framework/utils/constants';
import type { NextPage } from 'next';
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

export type NextPageWithLayout<P = {}> = NextPage<P> & {
  authenticationRequired?: boolean;
  getLayout?: (page: ReactElement) => ReactNode;
};

export interface GetParams {
  slug: string;
  language?: string;
}

export interface Success {
  success: boolean;
  message: string;
}

export type LayoutProps = {
  readonly children: ReactNode;
};

export interface HomePageProps {
  variables: {
    products: any;
    popularProducts?: any;
    bestSellingProducts?: any;
    categories: any;
    types: any;
    group_id: any;
    /**
     * Active category ids linked to this type, including nested children.
     * The type product grid matches these in addition to the product's own type.
     */
    typeCategoryIds?: number[];
    layoutSettings: any;
    /** First page of the type grid, rendered before InstantSearch starts. */
    ssrProducts?: ISearchProduct[];
    /**
     * First page of each home `DYNAMIC_PRODUCTS` shelf, parallel to
     * `listDynamicProductSections(layoutSettings.sections)` (same order).
     */
    ssrShelfProducts?: ISearchProduct[][];
  };
  layout: string;
  isRootPage?: boolean;
  typeSeo?: TypeSeo | null;
}

export interface IProductAvailabilityNotification {
  id?: number;
  email?: string;
  product_id?: number;
}

export interface SearchParamOptions {
  types: string;
  'types.slug': string;
  type: string;
  name: string;
  categories: string;
  tags: string;
  author: string;
  price: string;
  manufacturer: string;
  status: string;
  is_active: string;
  shop_id: string;
  min_price: string;
  max_price: string;
  rating: string;
  question: string;
  notice: string;
  faq_type: string;
  issued_by: string;
  title: string;
  target: string;
  shops: string;
  visibility: string;
  page: number;
}

export interface QueryOptions {
  page?: number;
  limit?: number;
}

export interface RefundPolicyQueryOptions extends QueryOptions {
  title: string;
  target: 'vendor' | 'customer';
  status: 'approved' | 'pending';
  orderBy: string;
  sortedBy: string;
}

export interface PaginatorInfo<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: any[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface Attachment {
  id: number;
  original: string;
  thumbnail: string;
  __typename?: string;
  slug?: string;
}

export interface ProductQueryOptions extends QueryOptions {
  shop_id: string;
  sortedBy: string;
  orderBy: string;
  name: string;
  categories: string;
  tags: string;
  type: string;
  manufacturer: string;
  author: string;
  price: string;
  min_price: string;
  max_price: string;
  language: string;
  searchType: string;
  searchQuery: string;
  text: string;
  visibility: string;
}

export interface PopularProductQueryOptions extends QueryOptions {
  language: string;
  type_slug: string;
  with: string;
  range: number;
}

export interface BestSellingProductQueryOptions extends QueryOptions {
  language: string;
  type_slug: string;
  with: string;
  range: number;
}

export interface CategoryQueryOptions extends QueryOptions {
  type: string;
  with?: string;
}

export interface RefundQueryOptions extends QueryOptions {
  language: string;
  parent: string | null;
  type: string;
}

export interface TagQueryOptions extends QueryOptions {
  parent: string | null;
  type: string;
}

export interface TypeQueryOptions extends QueryOptions {
  language: string;
  name: string;
  orderBy: any;
}

export interface ShopQueryOptions extends QueryOptions {
  name: string;
  is_active: number;
}

export interface AuthorQueryOptions extends QueryOptions {
  language: string;
  name: string;
  orderBy: string;
  type?: string;
}

export interface ManufacturerQueryOptions extends QueryOptions {
  name?: string;
  orderBy?: string;
  language: any;
  type?: string;
}

export interface FaqsQueryOptions extends QueryOptions {
  faq_title: string;
  issued_by: string;
  faq_type: string;
  orderBy: string;
  shop_id: string;
}

export interface FlashSaleQueryOptions extends QueryOptions {
  title?: string;
  shop_id?: string;
}

export interface FlashSaleProductsQueryOptions extends QueryOptions {
  slug: string;
}

export interface TermsAndConditionsQueryOptions extends QueryOptions {
  title: string;
  issued_by: string;
  type: string;
  orderBy: string;
  shop_id: string;
  is_approved: boolean;
}

export interface CouponQueryOptions extends QueryOptions {
  name: string;
  orderBy: string;
  code: string;
  shop_id: string;
}

export interface OrderQueryOptions extends QueryOptions {
  name: string;
  orderBy: string;
  sortedBy?: string;
  searchJoin?: string;
  user_id?: string;
}

export interface ReviewQueryOptions extends QueryOptions {
  product_id: string;
  rating?: string;
  orderBy?: string;
  sortedBy?: string;
}

export interface QuestionQueryOptions extends QueryOptions {
  product_id: string;
  question?: string;
}

export interface MyQuestionQueryOptions extends QueryOptions {}

export interface MyReportsQueryOptions extends QueryOptions {
  language: any;
}

export interface SettingsQueryOptions extends QueryOptions {}

export interface WishlistQueryOptions extends QueryOptions {}

export interface ProductPrice {
  price: number;
  sale_price: number;
  product_id: number;
  price_channel_id: number;
}

export interface Product {
  id: string;
  product_id: number;
  name: string;
  slug: string;
  sku: string;
  author: Author;
  manufacturer: Manufacturer;
  tags: Tag[];
  is_digital: boolean;
  is_external: boolean;
  external_product_url: string;
  external_product_button_text: string;
  product_type: string;
  description: string;
  type: Type;
  /** Navigation types when API returns the relation; otherwise use `type`. */
  types?: Type[];
  /** Navigation type ids (same order as `types` when present). */
  types_id?: number[];
  price: number;
  sale_price: number;
  min_price: number;
  max_price: number;
  image: Attachment;
  status: string;
  gallery: Attachment[];
  shop: Shop;
  unit: string;
  categories: Category[];
  quantity: number;
  in_stock?: number;
  total_reviews: number;
  ratings: number;
  in_wishlist: boolean;
  variations: object[];
  variation_options: object[];
  rating_count: RatingCount[];
  related_products: Product[];
  created_at: string;
  updated_at: string;
  language: string;
  video?: {
    url: string;
  }[];
  in_flash_sale: boolean;
  prices: ProductPrice[];
}

export interface IProductPrice {
  channel: string;
  price: number;
  price_channel_id: number;
  product_id: number;
  sale_price: number;
}

export interface ISearchProduct {
  id: string; // same as sku
  discount?: number; // TODO: remove after review
  brand: string;
  category: string;
  quantity?: number;
  in_stock?: number;
  status?: string;
  characteristics?: {
    id: number;
    value: string;
    valueJson: Record<string, any>;
    name: string;
  }[];
  gallery?: {
    file_name: string;
    id: number;
    original: string;
    thumbnail: string;
  }[];
  image?: {
    file_name: string;
    id: number;
    original: string;
    thumbnail: string;
  };
  manufacturer: string;
  name: string;
  price: number;
  cashback_price?: number;
  sale_price: number;
  product_id: number;
  product_prices?: IProductPrice[];
  sku: string;
  slug: string;

  // TODO: review if exists and if is needed, cause some items has those values as required
  is_promotional_pack?: boolean;
  promotional_pack_quantity?: number;
  promotional_pack_unit_price?: number;
  apply_promotional_pack?: number;

  subtotal?: number;
  unit_price?: number;
  original_sale_price?: number;
  unit: any;
  is_digital?: any;
  language?: any;
  in_flash_sale?: any;
  shop?: any;
  type_id?: any;
  /** All navigation type ids when available (e.g. REST); primary remains `type_id`. */
  type_ids?: (string | number)[];
  /** Numeric navigation type ids (Typesense / API). */
  types_id?: number[];
  product_type?: string;
  min_price?: number;
  max_price?: number;
  ratings?: number;
  type?: any;
  types?: Type[];
  categories?: Category[];

  /** Product variant selector (dosage, quantity, etc.) */
  variant_group_id?: number;
  variant_label?: string;
  variant_type?: 'dosage' | 'quantity' | 'custom' | string;
  variant_type_label?: string;
}

export interface IBuyTogetherProduct {
  id: string;
  product_id: number;
  name: string;
  slug: string;
  sku: string;
  quantity: number;
  in_stock: number;
  price: number;
  sale_price: number;
  image?: ISearchProduct['image'];
  manufacturer?: string;
  shop?: Pick<Shop, 'id'>;
  unit?: ISearchProduct['unit'];
}

export interface ICartProduct {
  id: string; // sku
  sku: string;
  product_id: number;
  name: string;
  type_id?: string;
  /** Navigation type ids for cart lines when catalog exposes multiple types. */
  type_ids?: string[];
  types_id?: number[];
  slug: string;
  unit?: string;
  is_digital?: boolean;
  variationId?: string | number;
  image?: string;
  stock?: number;
  price?: number;
  sale_price: number;
  language?: string;
  in_flash_sale?: boolean;
  shop_id?: string;
  characteristics?: {
    id: number;
    value: string;
    valueJson: Record<string, any>;
    name: string;
  }[];
  product_prices: IProductPrice[];
  cashback_price?: number;
  is_promotional_pack?: boolean;
  promotional_pack_quantity?: number;
  promotional_pack_unit_price?: number;
  apply_promotional_pack?: boolean;
  pbm_info?: PBMInfo;
  // GTM / analytics (from product at add-to-cart time)
  category?: string;
  /** When present (from product.categories), used for coupon allowed_category_ids matching. */
  category_ids?: number[];
  brand?: string;
  // Calculated Items / virtual fields
  calculated_sale_price: number; // TODO: tirar ou não
  subtotal?: number;
  quantity?: number;
  discount?: number;
  unit_price?: number;
  /** ISO timestamp for last-write-wins cart sync (per line) */
  cart_line_updated_at?: string;
  /** Set when the line was added via /compra-com-receita (prescription analysis flow). */
  from_prescription?: boolean;
}

export interface RatingCount {
  rating: number;
  total: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  details?: string;
  image: Attachment;
  parent_id?: number | null;
  children?: Category[];
}

export interface Banner {
  id: string;
  title: string;
  description: string;
  image: Attachment;
}

export interface TypeSeo {
  id: string | number;
  name: string;
  slug: string;
  banners?: Banner[];
}

export interface Type {
  id: string;
  name: string;
  slug?: any;
  banners: Banner[];
  icon?: string;
  promotional_sliders: Attachment[];
  settings: {
    isHome: boolean;
    layoutType: string;
    productCard?: string;
    bestSelling: {
      enable?: boolean;
      title?: string;
    };
    popularProducts: {
      enable?: boolean;
      title?: string;
    };
    category: {
      enable?: boolean;
      title?: string;
    };
    handpickedProducts: {
      enable?: boolean;
      title?: string;
      products?: Product[];
      enableSlider?: boolean;
    };
    newArrival: {
      enable?: boolean;
      title?: string;
    };
    authors: {
      enable?: boolean;
      title?: string;
    };
    manufactures: {
      enable?: boolean;
      title?: string;
    };
  };
}

export interface ShopAddress {
  country: string;
  city: string;
  state: string;
  zip: string;
  street_address: string;
}

export interface Shop {
  __typename?: string;
  id: string;
  name: string;
  slug: string;
  description: string;
  cover_image: Attachment;
  logo?: Attachment;
  is_active?: boolean;
  distance?: number;
  address: UserAddress;
  settings?: {
    contact?: string;
    socials?: any;
    location?: GoogleMapLocation;
    website?: string;
    isShopUnderMaintenance: boolean;
    shopMaintenance: {
      image: Attachment;
      title: string;
      description: string;
      start: string;
      until: string;
    };
  };
  notifications?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
}

export interface Author {
  id: string;
  name: string;
  slug: string;
}

export interface IOrderStatus {
  id: number;
  name: string;
  slug: string;
}

export interface Manufacturer {
  id: string;
  name: string;
  slug: string;
}

export enum CouponType {
  FIXED = 'fixed',
  PERCENTAGE = 'percentage',
  FREE_SHIPPING = 'free_shipping',
}

/** Subset returned by verify-coupon for line-level discount allocation (matches API create-order). */
export interface CouponItemEligibilityProperties {
  allowed_skus?: string[];
  allowed_category_ids?: number[];
  minimum_allowed_amount?: number;
  /** When true the coupon does not stack with payment-method (e.g. Pix) discounts. */
  non_cumulative?: boolean;
}

export interface Coupon {
  id: string;
  code: string;
  description: string;
  translated_languages: string[];
  orders: Order[];
  type: string;
  image: Attachment;
  amount: number;
  minimum_cart_amount?: number;
  active_from: string;
  expire_at: string;
  created_at: string;
  updated_at: string;
  target?: boolean;
  shop_id?: string;
  is_approve?: boolean;
  is_valid?: boolean;
  properties?: CouponItemEligibilityProperties;
}

/** Where a listed coupon comes from, as returned by `POST /coupons/available`. */
export type AvailableCouponSource =
  | 'roulette'
  | 'bulk'
  | 'granted'
  | 'email_list'
  | 'indication'
  | 'public';

export type AvailableCouponRuleKind =
  | 'minimum_cart'
  | 'eligible_products'
  | 'minimum_item_amount'
  | 'single_use'
  | 'uses_per_customer'
  | 'first_order'
  | 'non_cumulative';

export type AvailableCouponLockCode =
  | 'cart_below_minimum'
  | 'no_eligible_items'
  | (string & {});

export interface AvailableCoupon {
  key: string;
  /** Code to send to verify (unique code for roulette/bulk instances). */
  code: string;
  coupon_id: number;
  coupon_code_id?: number;
  source: AvailableCouponSource;
  type: `${CouponType}`;
  amount: number;
  minimum_cart_amount: number;
  description: string | null;
  image?: Attachment | null;
  /** ISO date; earliest of the unique code and the coupon expiry. */
  expires_at: string;
  rules: { kind: AvailableCouponRuleKind; label: string }[];
  status: 'eligible' | 'locked';
  lock?: {
    code: AvailableCouponLockCode;
    message: string;
    missing_amount?: number;
  };
  eligible_subtotal: number;
  non_cumulative: boolean;
}

export interface AvailableCouponsResponse {
  coupons: AvailableCoupon[];
}

export enum OwnerType {
  USER = 'user',
  RELATIVE = 'relative',
}

export enum RecipeType {
  PHYSIC = 'physic',
  DIGITAL = 'digital',
}

export interface RecipeBase {
  id: string;
  date: string;
  ownerType: OwnerType;
  type: RecipeType;
}

export interface DigitalRecipe extends RecipeBase {
  type: RecipeType.DIGITAL;
  token: string;
}

export interface FisicaRecipe extends RecipeBase {
  type: RecipeType.PHYSIC;
  file: Attachment;
  file_name: string;
}

export interface UsuarioRecipe {
  ownerType: OwnerType.USER;
  rg: string;
}

export interface RelativeRecipe {
  ownerType: OwnerType.RELATIVE;
  relative_name: string;
  relative_birthdate: string;
}

export type Recipe = (DigitalRecipe | FisicaRecipe) &
  (UsuarioRecipe | RelativeRecipe);

export interface Tag {
  id: string;
  name: string;
  slug: string;
}

export interface Feedback {
  id: string;
  user_id: string;
  model_type: string;
  model_id: string;
  positive: boolean;
  negative: boolean;
  created_at: string;
  updated_at: string;
}
export interface Maintenance {
  image: any;
  title: string;
  description: string;
  start: string;
  until: string;
}

export interface Settings {
  id: string;
  name: string;
  slug: string;
  options: {
    [key: string]: any;
  };
}

export interface SetupIntentInfo {
  client_secret?: string;
  intent_id?: string;
}

export interface PaymentIntentInfo {
  client_secret: string;
  payment_id: string;
  is_redirect: boolean;
  redirect_url: string;
  currency: string;
  amount: string;
}

export interface GatewayData {
  status?: string;
  wallet?: string;
  pix_psp?: string;
  qr_code?: string;
  deep_link?: string;
  qr_code_text?: string;
  gateway_order_id?: string;
}

export type TOrderItems = {
  id?: number;
  created_at?: string;
  updated_at?: string;
  order_quantity: number;
  original_unit_price: number;
  unit_price: number;
  discount?: number;
  subtotal?: number;
  order_id?: number;
  product_id?: number;
  sku?: string;
  custom_properties?: Record<string, unknown> | null;
  product?: {
    id?: number;
    created_at?: string;
    updated_at?: string;
    name?: string;
    slug?: string;
    pivot?: null | any;
    description?: string;
    in_stock?: number;
    is_taxable?: number;
    sku?: string;
    gallery?: any[];
    image?: {
      id?: string;
      original?: string;
      file_name?: string;
      thumbnail?: string;
    };
    height?: string;
    length?: string;
    width?: string;
    quantity?: number;
    unit?: string;
    ratings?: null | any;
    in_wishlist?: number;
    weight?: null | any;
    tax_code?: null | any;
    order_limit?: null | any;
    type_id?: number;
    manufacturer_id?: null | number;
    product_type_id?: number;
    product_status_id?: number;
    shop_id?: number;
  };
};

export interface PaymentIntent {
  id: number | string;
  status?: string;
  order_id: number | string;
  payment_gateway: PaymentGateway;
  tracking_number: string;
  payment_intent_info: PaymentIntentInfo;
  gateway_data?: GatewayData;
  gatewayResponse?: {
    gateway_data?: GatewayData;
  };
}

/** Prescription / recipe request persisted on an order (shop API). */
export interface OrderRequestAttachment {
  id?: number;
  name?: string;
  path?: string;
  size?: number;
  mime_type?: string;
  user_id?: number;
}

export interface OrderRequestUserRelationship {
  id?: number;
  name?: string;
  relationship?: string;
  document?: string;
  document_type?: string;
  birth_date?: string | null;
  user_id?: number;
}

export interface OrderRequest {
  id: number;
  name?: string;
  document?: string;
  type?: string;
  date?: string;
  token?: string;
  order_id?: number;
  attachment_id?: number | null;
  user_relationship_id?: number | null;
  attachment?: OrderRequestAttachment;
  user_relationship?: OrderRequestUserRelationship;
  order_item_requests?: unknown[];
}

export interface OrderInvoice {
  id: number;
  access_key: string;
  model: string | null;
  series: string | null;
  number: string | null;
  created_at: string;
}

export interface Order {
  id: number | string;
  tracking_number: string;
  requires_approval?: boolean;
  customer_id: number | string;
  customer_name: string;
  customer_contact: string;
  customer?: User;
  amount: number;
  subtotal?: number;
  children: Order[];
  sales_tax: number;
  total: number;
  paid_total: number;
  coupon?: Coupon;
  discount?: number;
  delivery_fee?: number;
  delivery_time?: string;
  delivery_subtime?: string;
  products: Product[];
  created_at: Date;
  updated_at: Date;
  billing_address?: TUserAddressFormValues;
  shipping_address?: TUserAddressFormValues;
  refund: Refund;
  language?: string;
  payment_intent?: PaymentIntent;
  order_status?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  payment_status_id: number;
  payment_status?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  payment_gateway?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  payments?: Array<{
    payment_gateway_id: number;
    amount: number;
    intent: any;
  }>;
  order_items?: Array<TOrderItems>;
  order_requests?: OrderRequest[];
  reviews?: Review[];
  user?: {
    name?: string;
  };
  order_type_id?: OrderType;
}

export enum OrderType {
  ORDER = 1,
  RECHARGE = 2,
}

export interface VerifyCouponInputType {
  code: string;
  sub_total: number;
  order_items: TOrderItems[];
}

export interface VerifyCouponResponse {
  is_valid: boolean;
  coupon?: Coupon;
  /** Present when the verified code is a unique bulk `coupon_code` row. */
  coupon_code_id?: number;
  message?: string;
}

export interface CreateReviewInput {
  product_id: string;
  shop_id: string;
  order_id: string;
  variation_option_id: string;
  comment?: string;
  rating: number;
  photos?: Attachment[];
}

export interface CreateOrderReviewInput {
  order_rating: number;
  delivery_rating: number;
  comment?: string;
  photos?: string[];
}

export interface OrderReview {
  id: number;
  order_id: number;
  user_id: number;
  order_rating: number;
  delivery_rating: number;
  comment: string | null;
  photos: string[] | null;
  created_at: string;
  updated_at: string;
  is_low_rating?: boolean;
}

export interface UpdateReviewInput extends CreateReviewInput {
  id: string;
}

export interface ReviewResponse {
  product_id: string;
}

export interface CreateRefundInput {
  order_id: string;
  title: string;
  description: string;
  refund_reason_id: string;
  images: Attachment[];
}

export interface CreateOrderPaymentInput {
  tracking_number: string;
  payment_gateway: string;
}

export interface CreateFeedbackInput {
  model_id: string;
  model_type: string;
  positive?: boolean;
  negative?: boolean;
}

export interface CreateQuestionInput {
  question: string;
  product_id: string;
  shop_id: string;
}

export interface CreateAbuseReportInput {
  model_id: string;
  model_type: string;
  message: string;
}

export interface Feedback {
  id: string;
  user_id: string;
  model_type: string;
  model_id: string;
  positive: boolean;
  negative: boolean;
  created_at: string;
  updated_at: string;
}

export interface Refund {
  id: string;
  title: string;
  description: string;
  images: Attachment[];
  amount: number;
  status: RefundStatus;
  shop: Shop;
  order: Order;
  refund_reason: RefundReason;
  customer: User;
  created_at: string;
  updated_at: string;
}
export interface RefundPolicy {
  id: string;
  title: string;
  slug: string;
  target: string;
  status: string;
  description?: string;
  language: string;
  shop_id?: string;
  shop?: Shop;
  refunds?: Refund[];
  translated_languages: Array<string>;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface RefundReason {
  id: string;
  name: string;
  slug: string;
  language: string;
  created_at: string;
  updated_at: string;
}

export enum PaymentGateway {
  STRIPE = 'STRIPE',
  COD = 'CASH_ON_DELIVERY',
  CASH = 'CASH',
  FULL_WALLET_PAYMENT = 'FULL_WALLET_PAYMENT',
  PAYPAL = 'PAYPAL',
  MOLLIE = 'MOLLIE',
  RAZORPAY = 'RAZORPAY',
  SSLCOMMERZ = 'SSLCOMMERZ',
  PAYSTACK = 'PAYSTACK',
  PAYMONGO = 'PAYMONGO',
  XENDIT = 'XENDIT',
  IYZICO = 'IYZICO',
  BKASH = 'BKASH',
  FLUTTERWAVE = 'FLUTTERWAVE',
  PIX = 'PIX',
  CREDIT_CARD = 'CREDIT_CARD',
  PICPAY = 'PICPAY',
  MERCADO_PAGO = 'MERCADO_PAGO',
  BITCOIN = 'BITCOIN',
}

export enum OrderStatus {
  PENDING = 'order-pending',
  PROCESSING = 'order-processing',
  COMPLETED = 'order-completed',
  CANCELLED = 'order-cancelled',
  REFUNDED = 'order-refunded',
  FAILED = 'order-failed',
  AT_LOCAL_FACILITY = 'order-at-local-facility',
  OUT_FOR_DELIVERY = 'order-out-for-delivery',
}

export enum PaymentStatus {
  PENDING = 'payment-pending',
  PROCESSING = 'payment-processing',
  SUCCESS = 'payment-success',
  FAILED = 'payment-failed',
  REVERSAL = 'payment-reversal',
  REFUNDED = 'payment-refunded',
  COD = 'cash-on-delivery',
  AWAITING_FOR_APPROVAL = 'payment-awaiting-for-approval',
}

export enum RefundStatus {
  APPROVED = 'Approved',
  PENDING = 'Pending',
  REJECTED = 'Rejected',
  PROCESSING = 'Processing',
}

export interface Address {
  id: string;
  title: string;
  type: any;
  address: {
    __typename?: string;
    country: string;
    city: string;
    state: string;
    zip: string;
    street_address: string;
  };
  location: GoogleMapLocation;
  default?: boolean;
}

export type TAddressFormValues = {
  city: string;
  number: number;
  state: string;
  zip: string;
  street_address: string;
  county: string;
  reference: string;
  complement: string;
};

export type TUserAddressFormValues = {
  id?: string;
  user_id?: number;
  title: string;
  type: AddressType;
  address: TAddressFormValues;
  location?: GoogleMapLocation;
  default?: boolean;
};

export interface User {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  social_name?: string;
  email: string;
  is_active?: boolean;
  receive_email?: boolean;
  receive_sms?: boolean;
  gender?: string;
  document?: string;
  phone?: string;
  cellphone?: string;
  birth_date?: string;
  cpf?: string;
  wallet: {
    total_points: number;
    points_used: number;
    available_points: number;
  };
  profile: {
    id?: string;
    contact?: string;
    bio?: string;
    picture?: string;
    avatar?: Attachment;
  };
  addresses: TUserAddressFormValues[] | undefined;
  payment_gateways?: UserPaymentGateway[];
  last_order: Order;
}

export interface UserPaymentGateway {
  id: number | string;
  customer_id: string;
  gateway_name: PaymentGateway;
  user_id: number | string;
}

export interface UpdateUserInput extends Partial<User> {
  id: number;
}

export interface LoginUserInput {
  email: string;
  password: string;
}

export interface CPFUserInput {
  cpf: string;
}

export type SocialLoginInputType = {
  provider: string;
  access_token: string;
  user?: Partial<User>;
};

export type GoogleMobileLoginInputType = {
  idToken: string;
  userEmail?: string;
};

export type SendOtpCodeInputType = {
  phone_number: string;
};

export interface RegisterUserInput {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  repeat_password: string;
  cellphone?: string;
  phone: string;
  social_name?: string;
  cpf: string;
  birth_date: string;
  gender: string;
  accepted_terms: boolean;
  receive_email: boolean;
  invited_by_referral_code?: string;
  turnstile_token?: string;
}

export interface RegisterUserOutput {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  cellphone?: string;
  phone: string;
  social_name?: string;
  cpf: string;
  birth_date: string;
  gender: string;
  receive_email: boolean;
  referral_code?: string;
}

export interface ForgotPasswordUserInput {
  email: string;
}

/** Contact option for reset password (masked email or phone) */
export interface ResetPasswordContactOption {
  type: 'email' | 'cellphone' | 'phone';
  masked: string;
}

export interface ResetPasswordOptionsResponse {
  emails: ResetPasswordContactOption[];
  phones: ResetPasswordContactOption[];
}

/** Payload for sending reset password link/code */
export interface SendResetPasswordInput {
  cpf: string;
  contactType: 'email' | 'cellphone' | 'phone';
}

export interface ResetPasswordUserInput {
  token: string;
  password: string;
}

export interface ValidateResetTokenInput {
  token: string;
}

export interface VerifyForgotPasswordUserInput {
  token: string;
  email: string;
}

export interface ChangePasswordUserInput {
  new_password: string;
  password: string;
}
export interface UpdateEmailUserInput {
  email: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
}

export interface PasswordChangeResponse extends Success {}

export interface EmailChangeResponse extends Success {}
export interface VerificationEmailUserInput extends Success {
  email: string;
}

export interface AuthResponse {
  token: string;
  refresh_token: string;
  permissions: string[];
}

export interface IAuthProfile {
  first_name?: string;
  last_name?: string;
  cellphone?: string;
  phone?: string;
  social_name?: string;
  cpf?: string;
  email?: string;
  gender?: string;
  birth_date?: string;
}

export interface QuotationResponse {
  delivery_method_id: number;
  final_shipping_cost: number;
  free_shipping_message: string;
  delivery_time: string;
  delivery_subtime: string;
  delivery_estimated_duration: string;
}

export interface PrescriptionCheckInput {
  zip_code: string;
  order_total: number;
  products: any[];
}

export enum PrescriptionAcceptanceType {
  DigitalOnly = 1,
  DigitalAndFisic = 2,
}

export interface PrescriptionCheckResponse {
  data: {
    prescriptionAcceptance: {
      type: number;
      description: string;
    };
    status: string;
  };
}

export type PrescriptionAnalysisStatus =
  | 'uploading'
  | 'extracting'
  | 'matching'
  | 'completed'
  | 'failed';

export type PrescriptionMatchType =
  | 'exact'
  | 'same_drug_different_dosage'
  | 'same_drug_different_form'
  | 'same_drug_different_packaging'
  | 'name_only'
  | 'no_match';

export interface PrescriptionMatchResult {
  id: string;
  typesense_document_id: string;
  product_id: number | null;
  product_name: string;
  match_confidence: number;
  match_type: PrescriptionMatchType | string;
  product_pack_size: number | null;
  prescribed_unit_count: number | null;
  suggested_quantity: number | null;
  rank: number;
  is_generic_alternative: boolean;
}

export interface PrescriptionExtractedItem {
  id: string;
  medication_name: string;
  dosage: string | null;
  pharmaceutical_form: string | null;
  quantity: string | null;
  raw_text: string | null;
  extraction_confidence: number;
  match_results: PrescriptionMatchResult[];
}

export interface PrescriptionAnalysisResult {
  id: string;
  status: PrescriptionAnalysisStatus;
  error_message: string | null;
  failure_stage: string | null;
  error_code: string | null;
  failure_stage_label: string | null;
  attachment_url: string | null;
  attachment_id: number | null;
  file_name: string | null;
  raw_extraction_json: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  created_at: string;
  extracted_items?: PrescriptionExtractedItem[];
}

export interface PrescriptionUploadResponse {
  s3Key: string;
  file_name: string;
  mime_type: string;
  size: number;
}

export interface CreatePrescriptionAnalysisInput {
  s3_key: string;
  file_name: string;
  mime_type: string;
  size: number;
}

export interface CreatePrescriptionAnalysisResponse {
  id: string;
  status: PrescriptionAnalysisStatus;
  attachment_id: number;
}

export type PrescriptionAnalysisStep =
  | 'upload'
  | 'analyzing'
  | 'products'
  | 'ownership'
  | 'review';

export interface PrescriptionSelectedLine {
  extractedItemId: string;
  matchId: string;
  productId: number | null;
  productName: string;
  quantity: number;
  selected: boolean;
}

export interface PrescriptionFileMeta {
  file_name: string;
  mime_type: string;
  s3_key: string;
  attachment_url?: string;
}

export interface OTPResponse {
  message: string;
  success: boolean;
  provider: string;
  id: string;
  phone_number: string;
  is_contact_exist: boolean;
}

export interface VerifyOtpInputType {
  phone_number: string;
  code: string;
  otp_id: string;
}

export interface OtpLoginInputType {
  phone_number: string;
  code: string;
  otp_id: string;
  name?: string;
  email?: string;
}

export interface OTPVerifyResponse {
  success: string;
  message: string;
}

export interface DigitalFile {
  id: string;
  fileable: Product;
}

export interface DownloadableFile {
  id: string;
  purchase_key: string;
  digital_file_id: string;
  customer_id: string;
  file: DigitalFile;
  created_at: string;
  updated_at: string;
}

export interface CreateContactUsInput {
  name: string;
  email: string;
  subject: string;
  description: string;
  emailTo?: string;
  isChecked?: boolean;
}

export interface ExtendedContactUsInput extends CreateContactUsInput {
  emailTo: string;
}

export interface CardInput {
  number: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  email?: string;
}

// enum PaymentGatewayType {
//   STRIPE = 'Stripe',
//   CASH_ON_DELIVERY = 'Cash on delivery',
//   CASH = 'Cash',
//   FULL_WALLET_PAYMENT = 'Full wallet payment',
// }

export interface IUserWallet {
  balance: number;
}

export interface IUserStatement {
  id: number;
  created_at: string;
  updated_at: string;
  amount: number;
  type: 'credit' | 'debit';
  balance: number;
  description: string;
  icon: string;
  user_id: number;
}

export interface ICreditCard {
  id?: number;
  /** Saved card reference sent on payment. */
  credit_card_id?: number;
  holder_name?: string;
  card_number?: string;
  cvv?: string;
  token?: string;
  expiration_date?: string;
  expiration_year?: string;
  expiration_month?: string;
  save_card?: boolean;

  installments?: number;
  brand?: string;
  end?: string;

  // internal validation
  validate?: boolean;
  isValid?: boolean;
}

export interface IOrderInput {
  tracking_number?: string;
  customer_contact: string;
  discount?: number;
  amount: number;
  sales_tax: number;
  total: number;
  paid_total: number;
  delivery_fee: number;
  delivery_time: string;
  delivery_method_id: number;
  billing_address: Address;
  shipping_address: Address;
  payment_intent: PaymentIntent | {};
  altered_payment_gateway?: string;
  user_id: number;
  coupon_id?: number;
  shop_id: number | string;
  order_status_id?: number;
  parent_order_id?: number;
  payment_status_id?: number;
  wallet_amount?: number;
  payable_amount?: number;
  order_items: ICartProduct[];
  order_requests: any[];

  credit_card?: ICreditCard;
  clearsale_fingerprint?: string;
  checkout_metadata?: {
    utm?: {
      source?: string;
      medium?: string;
      campaign?: string;
      term?: string;
      content?: string;
    };
    referrer?: string;
    landing_page?: string;
    checkout_page?: string;
    client_session_id?: string;
    click_ids?: {
      gclid?: string;
      fbclid?: string;
      msclkid?: string;
    };
    client_channel?: 'browser' | 'pwa' | 'app_webview';
    shop_version?: string;
    cart_id?: string;
    cart_started_at?: string;
    cart_age_seconds?: number;
  };
  payments?: Array<{
    payment_gateway_id: number;
    amount: number;
    intent: unknown;
  }>;
}
export interface IOrderOutput extends Omit<IOrderInput, 'order_items'> {
  order_items: {
    order_quantity?: number;
    original_unit_price?: number;
    unit_price?: number;
    subtotal?: number;
    product_id?: number;
  }[];
}

export interface CreateOrderInput {
  order: IOrderInput;
  coupon_code?: string | null;
  pbm?: PBMVerifyPricesInput;
}

export interface CreateOrderOutput {
  order: IOrderOutput;
  coupon_code?: string | null;
  pbm?: PBMVerifyPricesInput;
}

export interface PaymentIntentCollection {
  tracking_number?: string;
  payment_intent_info?: PaymentIntentInfo;
  payment_gateway?: string;
}

export interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  photos: Attachment[];
  user: User;
  product: Product;
  shop: Shop;
  feedbacks: Feedback[];
  positive_feedbacks_count: number;
  negative_feedbacks_count: number;
  my_feedback: Feedback;
  created_at: string;
  updated_at: string;
}

export interface Question {
  id: string;
  answer: string;
  my_feedback: Feedback;
  negative_feedbacks_count: number;
  positive_feedbacks_count: number;
  question: string;
  created_at: string;
  updated_at: string;
  product: Product;
}

export interface Reports {
  id: string;
  message: string;
  created_at: string;
}

export interface ConnectProductOrderPivot {
  product_id: number;
  variation_option_id?: number;
  order_quantity: number;
  unit_price: number;
  subtotal: number;
}

export interface CheckoutVerificationInput {
  amount: number;
  products: ConnectProductOrderPivot[];
  billing_address?: Address;
  shipping_address?: Address;
}

export interface CepInput {
  cep: string;
}

export interface VerifiedCheckoutData {
  total_tax: number;
  shipping_charge: number;
  unavailable_products?: number[];
  wallet_currency?: number;
  wallet_amount?: number;
}

export interface Wishlist {
  id: string;
  product: Product;
  product_id: string;
  user: User[];
  user_id: string;
}

export interface UserAddress {
  street_address: string;
  country: string;
  city: string;
  state: string;
  zip: string;
  billing_address?: Address;
  shipping_address?: Address;
}
export interface GoogleMapLocation {
  lat?: number | string;
  lng?: number | string;
  street_number?: string;
  route?: string;
  street_address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  formattedAddress?: string;
  formatted_address?: string;
}
export interface ShopMapLocation {
  lat?: string;
  lng?: string;
  street_address?: string;
  formattedAddress?: string;
}

export enum StoreNoticePriorityType {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export interface StoreNotice {
  id: string;
  translated_languages: string[];
  priority: StoreNoticePriorityType;
  notice: string;
  description?: string;
  effective_from?: string;
  expired_at: string;
  type?: string;
  is_read?: boolean;
  shops?: Shop[];
  users?: User[];
  received_by?: string;
  created_by: string;
  expire_at: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  creator?: any;
}

export interface FAQS {
  id: string;
  faq_title: string;
  faq_description: string;
  slug: string;
  faq_type: string;
  issued_by: string;
  language: string;
  shop_id?: Shop;
  user_id: User;
  translated_languages: string[];
  created_at: string;
  updated_at: string;
}

export interface TermsAndConditions {
  id: string;
  translated_languages: string[];
  title: string;
  description: string;
  shop_id?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  is_approved?: boolean;
  issued_by?: string;
  type?: string;
}

export interface StoreNoticeQueryOptions extends QueryOptions {
  shop_id: string;
  shops?: string;
}

export interface FlashSale {
  id: string;
  title: string;
  slug: string;
  description: string;
  start_date: string;
  end_date: string;
  cover_image?: Attachment;
  image?: Attachment;
  type: string;
  rate: string;
  sale_status: boolean;
  sale_builder: any;
  language: string;
  translated_languages: string[];
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  products?: Product[];
}

export interface SingleFlashSale {
  flash_sale: FlashSale;
  products: ProductPaginator;
}

export enum ProductType {
  Simple = 'simple',
  Variable = 'variable',
}

export enum DiscountType {
  Percentage = 'percentage',
  FixedRate = 'fixed_rate',
}

export interface CategoryPaginator extends PaginatorInfo<Category> {}

export interface RefundReasonPaginator extends PaginatorInfo<RefundReason> {}
export interface ProductPaginator extends PaginatorInfo<Product> {}

export interface CategoryPaginator extends PaginatorInfo<Category> {}

export interface ShopPaginator extends PaginatorInfo<Shop> {}

export interface AuthorPaginator extends PaginatorInfo<Author> {}

export interface ManufacturerPaginator extends PaginatorInfo<Manufacturer> {}

export interface FaqsPaginator extends PaginatorInfo<FAQS> {}

export interface TermsAndConditionsPaginator
  extends PaginatorInfo<TermsAndConditions> {}

export interface CouponPaginator extends PaginatorInfo<Coupon> {}

export interface StoreNoticePaginator extends PaginatorInfo<StoreNotice> {}

export interface TagPaginator extends PaginatorInfo<Tag> {}

export interface OrderPaginator {
  data: Order[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
  };
}

export interface PurchasedProduct {
  product_id: number;
  sku: string | null;
  /** Number of distinct orders that contained the product */
  purchase_count: number;
  total_quantity: number;
  last_purchased_at: string;
}

export interface PurchasedProductPaginator {
  data: PurchasedProduct[];
  pagination: OrderPaginator['pagination'];
}
export interface OrderStatusPaginator extends PaginatorInfo<OrderStatus> {}

export interface RefundPaginator extends PaginatorInfo<Refund> {}

export interface RefundPolicyPaginator extends PaginatorInfo<RefundPolicy> {}

export interface ReviewPaginator extends PaginatorInfo<Review> {}

export interface QuestionPaginator extends PaginatorInfo<Question> {}

export interface ReportsPaginator extends PaginatorInfo<Question> {}

export interface DownloadableFilePaginator
  extends PaginatorInfo<DownloadableFile> {}

export interface WishlistPaginator extends PaginatorInfo<Wishlist> {
  product?: Product;
}

export interface FlashSalePaginator extends PaginatorInfo<FlashSale> {}

export interface NotifyLogs {
  id: string;
  receiver: string;
  sender: string;
  notify_type: string;
  notify_receiver_type: string;
  is_read: boolean;
  notify_text: string;
  created_at: string;
}
export interface NotifyLogsQueryOptions extends QueryOptions {
  notify_type: string;
  notify_receiver_type?: string;
  sender?: string;
  receiver: string;
  set_all_read?: boolean;
  is_read?: boolean;
  orderBy: string;
  sortedBy: string;
}

export interface NotifyLogsPaginator extends PaginatorInfo<NotifyLogs> {}

export interface BecomeSellerCommission {
  commission: number;
  description: string;
  image: Attachment;
  level: string;
  max_balance: string;
  min_balance: number;
  sub_level: string;
}

export interface Commission {
  commission: number;
  created_at?: Date;
  description: string;
  id?: number;
  image?: Attachment;
  language?: string;
  level?: string;
  max_balance?: string | number;
  min_balance?: string | number;
  sub_level?: string;
  updated_at?: Date;
}

export interface BecomeSellerBanner {
  description: string;
  image: Attachment;
  newsTickerTitle: string;
  newsTickerURL: string;
  title: string;
  button1Name?: string;
  button1Link?: string;
  button2Name?: string;
  button2Link?: string;
}

export interface SellingStep {
  title: string;
  description: string;
  image: Attachment;
}

export interface BusinessPurpose {
  description: string;
  title: string;
  icon: {
    value: string;
  };
}

export interface BecomeSellerDashboardShowcase {
  title: string;
  description: string;
  image: Attachment;
  buttonName?: string;
  buttonLink?: string;
  button2Name?: string;
  button2Link?: string;
}

export interface BecomeSellerPageOptions {
  banner: BecomeSellerBanner;
  commissionDescription: string;
  commissionTitle: string;
  defaultCommissionDetails: string;
  defaultCommissionRate: number;
  faqDescription: string;
  faqTitle: string;
  isMultiCommissionRate: boolean;
  faqItems: {
    description: string;
    title: string;
  }[];
  purposeDescription: string;
  purposeItems: BusinessPurpose[];
  purposeTitle: string;
  sellingStepsDescription: string;
  sellingStepsItem: SellingStep[];
  sellingStepsTitle: string;
  dashboard: BecomeSellerDashboardShowcase;
  guidelineTitle: string;
  guidelineDescription: string;
  guidelineItems: {
    title: string;
    link?: string;
  }[];
  sellerOpportunity: BecomeSellerDashboardShowcase;
  userStoryTitle: string;
  userStoryDescription?: string;
  userStories: {
    title: string;
    description: string;
    link: string;
    thumbnail?: Attachment;
  }[];
  contact: {
    title: string;
    description: string;
  };
}

export interface BecomeSeller {
  commission: BecomeSellerCommission[];
  page_options: {
    page_options: BecomeSellerPageOptions;
  };
}

export type AlertProps = {
  message: string | null;
  variant?:
    | 'info'
    | 'warning'
    | 'error'
    | 'success'
    | 'infoOutline'
    | 'warningOutline'
    | 'errorOutline'
    | 'successOutline';
  closeable?: boolean;
  onClose?: React.Dispatch<React.SetStateAction<any>>;
  className?: string;
  children?: React.ReactNode;
  childClassName?: string;
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  variant?: 'normal' | 'outline' | 'custom' | 'dark' | 'secondary';
  size?: 'big' | 'medium' | 'small' | 'extraSmall';
  active?: boolean;
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  href?: string;
}

export interface ShopMaintenanceEvent {
  shop_id: string;
  isMaintenance: boolean;
  isShopUnderMaintenance: boolean;
}

export interface CreateUserRelationshipInput {
  relative_id?: number;
  id?: number;
  name: string;
  relationship: string;
  document: string;
  document_type: string;
  birth_date: string;
}

// PBM Types
export interface PBMProgram {
  id: number;
  name: string;
  register_url: string;
  middleware: {
    id: number;
    slug: string;
  };
}

export interface PBMSalePrice {
  id: number;
  price: number;
  quantity: number;
  name?: string;
}

export interface PBMActivationSession {
  localNumber?: number;
  centralNumber?: number | string;
  attendanceHash?: string;
}

export interface PBMActivation {
  fields: Record<string, unknown>;
  url_accept_term?: string;
  url_text_accept_term?: string;
  flow_deviation?: string;
  session: PBMActivationSession;
}

export interface PBMVerifyResponse {
  success: boolean;
  program?: PBMProgram;
  sale_prices?: PBMSalePrice[];
  combo_sale_prices?: PBMSalePrice[];
  message?: string;
  code?: string;
  needs_activation?: boolean;
  activation?: PBMActivation;
}

export interface PBMVerifyInput {
  product_id: string;
  cpf: string;
}

export interface PBMActivateInput {
  sku: string;
  document: string;
  user_payload: Record<string, unknown>;
  activation_fields: Record<string, unknown>;
  session: PBMActivationSession;
  attempt: number;
  url_accept_term?: string;
  url_text_accept_term?: string;
}

// PBM Cart Item Information
export interface PBMInfo {
  flag: boolean;
  sale_price_id: number;
  cpf: string;
  quantity: number;
  program?: PBMProgram;
  promotion?: PBMSalePrice;
  /** Unit price before Interplayers combo verification */
  base_sale_price?: number;
  /** Whether a combo discount price is currently applied */
  combo_applied?: boolean;
  /** Last verified combo unit price from /cart-verify */
  combo_verified_price?: number;
}

export interface PBMVerifyPricesProgram {
  name: string;
  id?: number;
  sku?: string;
  sale_price: number;
  quantity?: number;
  status?: 'approved' | 'rejected' | 'pending' | 'expired';
}

export interface PBMVerifyPricesMiddleware {
  name: string;
  id?: number;
  programs: PBMVerifyPricesProgram[];
}

export interface PBMVerifyPricesDocument {
  document: string;
  middlewares: PBMVerifyPricesMiddleware[];
}

export interface PBMVerifyPricesInput {
  documents: PBMVerifyPricesDocument[];
}

export interface PBMVerifyPricesResponse {
  documents: PBMVerifyPricesDocument[];
}

export interface CartVerifyInput {
  pbm: PBMVerifyPricesInput;
}

export interface CartVerifyItemResponse {
  sku: string;
  sale_price: number;
}

export type PbmComboVerifyStatus =
  | 'idle'
  | 'validating'
  | 'applied'
  | 'error';

export interface AppleMobileLoginPayload {
  idToken: string;
  userEmail?: string;
}

export interface AppleMobileLoginResponse {
  token: string;
  refresh_token?: string;
  permissions?: any[];
}

export type SavingsSummaryPeriod = '6_months' | '12_months' | 'all';

export interface UserSavingsSummaryLifetime {
  total_saved: number;
  total_spent: number;
  would_have_spent: number;
}

export interface UserSavingsSummaryTimelineItem {
  period: string;
  saved: number;
  spent: number;
  would_have_spent: number;
  order_count: number;
}

export interface UserSavingsSummaryResponse {
  lifetime: UserSavingsSummaryLifetime;
  timeline: UserSavingsSummaryTimelineItem[];
}

export type SavingsSummaryQueryParams = {
  period?: SavingsSummaryPeriod;
  date_from?: string;
  date_to?: string;
};